import React, { useState, useCallback, useMemo } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import {
  Box,
  Typography,
  Stack,
  Pagination,
  CircularProgress,
  Grid,
  Chip,
  useTheme
} from '@mui/material'
import MyStethiAIIcon from '/src/components/Icons/MyStethiAIIcon'
import LocationOnIcon from '@mui/icons-material/LocationOn'
import LocalHospitalIcon from '@mui/icons-material/LocalHospital'
import AttachMoneyIcon from '@mui/icons-material/AttachMoney'
import WorkIcon from '@mui/icons-material/Work'
import SchoolIcon from '@mui/icons-material/School'
import PublicIcon from '@mui/icons-material/Public'
import HomeWorkIcon from '@mui/icons-material/HomeWork'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import JobModal from './JobModal'
import AIChatBot from './AIChatBot'
import AIJobCard from './AIJobCard'
import ChatBottomBar from './ChatBottomBar'
import { addChatMessage, sendChatMessage } from '/src/redux/aiJobSearch'
import { generateExampleQueries } from './helpers'

const JOBS_PER_PAGE = 10

// Helper to format currency
const formatCurrency = (value) => {
  if (!value || value <= 1) return null
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(value)
}

// Component to render JobListingArguments as chips
const SearchCriteriaChips = ({ jobListingArguments }) => {
  const theme = useTheme()

  if (!jobListingArguments) return null

  const chips = []

  // Specialties
  if (jobListingArguments.specialties?.length > 0) {
    jobListingArguments.specialties.forEach((specialty, idx) => {
      chips.push(
        <Chip
          key={`specialty-${idx}`}
          icon={<LocalHospitalIcon />}
          label={specialty}
          size="small"
          sx={{ backgroundColor: theme.palette.primary.light }}
        />
      )
    })
  }

  // Subspecialties
  if (jobListingArguments.subspecialties?.length > 0) {
    jobListingArguments.subspecialties.forEach((subspecialty, idx) => {
      chips.push(
        <Chip
          key={`subspecialty-${idx}`}
          icon={<LocalHospitalIcon />}
          label={subspecialty}
          size="small"
          variant="outlined"
          sx={{ borderColor: theme.palette.primary.main }}
        />
      )
    })
  }

  // Locations
  if (jobListingArguments.locations?.length > 0) {
    jobListingArguments.locations.forEach((location, idx) => {
      chips.push(
        <Chip
          key={`location-${idx}`}
          icon={<LocationOnIcon />}
          label={location.location}
          size="small"
          sx={{ backgroundColor: theme.palette.info.light }}
        />
      )
    })
  }

  // Salary
  if (jobListingArguments.salaryMinimum) {
    chips.push(
      <Chip
        key="salary-min"
        icon={<AttachMoneyIcon />}
        label={`Min ${formatCurrency(jobListingArguments.salaryMinimum)}`}
        size="small"
        sx={{ backgroundColor: theme.palette.success.light }}
      />
    )
  }

  if (jobListingArguments.salaryDesired) {
    chips.push(
      <Chip
        key="salary-desired"
        icon={<AttachMoneyIcon />}
        label={`Target ${formatCurrency(jobListingArguments.salaryDesired)}`}
        size="small"
        sx={{ backgroundColor: theme.palette.success.light }}
      />
    )
  }

  // Practice Types
  if (jobListingArguments.practiceTypes?.length > 0) {
    jobListingArguments.practiceTypes.forEach((type, idx) => {
      chips.push(
        <Chip
          key={`practice-${idx}`}
          icon={<WorkIcon />}
          label={type}
          size="small"
          sx={{ backgroundColor: theme.palette.warning.light }}
        />
      )
    })
  }

  // Academic Types
  if (jobListingArguments.academicTypes?.length > 0) {
    jobListingArguments.academicTypes.forEach((type, idx) => {
      chips.push(
        <Chip
          key={`academic-${idx}`}
          icon={<SchoolIcon />}
          label={type}
          size="small"
          sx={{ backgroundColor: theme.palette.secondary.light }}
        />
      )
    })
  }

  // Area Types
  if (jobListingArguments.areaTypes?.length > 0) {
    jobListingArguments.areaTypes.forEach((type, idx) => {
      chips.push(
        <Chip
          key={`area-${idx}`}
          icon={<HomeWorkIcon />}
          label={type}
          size="small"
          variant="outlined"
        />
      )
    })
  }

  // Visas
  if (jobListingArguments.visas?.length > 0) {
    jobListingArguments.visas.forEach((visa, idx) => {
      chips.push(
        <Chip
          key={`visa-${idx}`}
          icon={<PublicIcon />}
          label={visa}
          size="small"
          variant="outlined"
        />
      )
    })
  }

  // Boolean flags
  if (jobListingArguments.pslfCompatible) {
    chips.push(
      <Chip
        key="pslf"
        label="PSLF Compatible"
        size="small"
        color="success"
      />
    )
  }

  if (jobListingArguments.loanForgiveness) {
    chips.push(
      <Chip
        key="loan-forgiveness"
        label="Loan Forgiveness"
        size="small"
        color="success"
      />
    )
  }

  if (chips.length === 0) return null

  return (
    <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid rgba(0, 0, 0, 0.12)', backgroundColor: 'white' }}>
      <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
        Search Criteria:
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
        {chips}
      </Box>
    </Box>
  )
}

const AIJobInterface = () => {
  const theme = useTheme()
  const dispatch = useDispatch()
  const [currentPage, setCurrentPage] = useState(1)
  const [selectedJob, setSelectedJob] = useState(null)

  // Get AI job search state from Redux
  const {
    rankedJobs,
    jobListingArguments,
    loading,
    isAiTyping,
    conversationId,
    mobileDrawerState
  } = useSelector(state => state.aiJobSearch)

  const candidate = useSelector(state => state.candidate)

  const exampleQueries = useMemo(
    () => generateExampleQueries(candidate),
    [candidate]
  )

  const filteredJobs = rankedJobs || []

  // Pagination calculations
  const totalPages = Math.ceil(filteredJobs.length / JOBS_PER_PAGE)
  const startIndex = (currentPage - 1) * JOBS_PER_PAGE
  const currentJobs = filteredJobs.slice(startIndex, startIndex + JOBS_PER_PAGE)

  // Event handlers
  const handleJobClick = useCallback(rankedJob => {
    setSelectedJob(rankedJob)
  }, [])

  const handleCloseModal = useCallback(() => {
    setSelectedJob(null)
  }, [])

  const handlePageChange = useCallback((event, value) => {
    setCurrentPage(value)
  }, [])

  const handleExampleChipClick = useCallback((query) => {
    dispatch(addChatMessage({ type: 'user', message: query }))
    dispatch(sendChatMessage({ message: query, conversationId: undefined }))
  }, [dispatch])

  const showBottomBar = mobileDrawerState === 'minimized'

  return (
    <>
      <Grid
        container
        className="ai-job-results-container"
        sx={{
          height: '100%',
          maxHeight: 'calc(100vh - 64px)',
          marginRight: 0,
          transition: 'margin-right 0.3s ease',
        }}
      >
        {/* Main Panel - AI Results */}
        <Grid
          item
          xs={12}
          md={12}
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          {/* Header */}
          <Box
            sx={{
              px: 2,
              py: 2,
              borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
              backgroundColor: 'white',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Box>
              <Typography variant='h6' sx={{ color: theme.palette.ai.dark, fontWeight: 600 }}>
                Stethi Results
              </Typography>
            </Box>
          </Box>

          {/* Search Criteria Chips */}
          <SearchCriteriaChips jobListingArguments={jobListingArguments} />

          {/* Job List Content */}
          <Box sx={{ flex: 1, overflow: 'auto', display: 'flex', flexDirection: 'column' }}>
            {filteredJobs.length > 0 ? (
              <>
                {/* Results Header */}
                <Box sx={{ p: 2, pb: 0, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant='body1' fontWeight='medium' color='text.primary'>
                    Found {filteredJobs.length} job{filteredJobs.length !== 1 ? 's' : ''} matching your criteria
                  </Typography>
                  {totalPages > 1 && (
                    <Pagination
                      count={totalPages}
                      page={currentPage}
                      onChange={handlePageChange}
                      color='primary'
                      size='small'
                    />
                  )}
                </Box>

                {/* Job Cards */}
                <Stack
                  spacing={1}
                  sx={{
                    px: 2,
                    flex: 1,
                    pb: showBottomBar ? 'calc(60px + env(safe-area-inset-bottom))' : 2,
                    minHeight: 0,
                    overflow: 'auto',
                    '&::-webkit-scrollbar': {
                      width: '6px',
                    },
                    '&::-webkit-scrollbar-track': {
                      backgroundColor: 'rgba(0,0,0,0.1)',
                    },
                    '&::-webkit-scrollbar-thumb': {
                      backgroundColor: theme.palette.ai.main,
                      borderRadius: '3px',
                    }
                  }}
                >
                  {currentJobs.map(rankedJob => (
                    <AIJobCard
                      key={rankedJob.job?.id || rankedJob.jobId}
                      rankedJob={rankedJob}
                      isActive={selectedJob?.job?.id === rankedJob.job?.id}
                      onClick={() => handleJobClick(rankedJob)}
                      conversationId={conversationId}
                    />
                  ))}
                </Stack>

                {/* Bottom Pagination */}
                {totalPages > 1 && (
                  <Box
                    sx={{
                      p: 2,
                      pb: showBottomBar ? 'calc(60px + env(safe-area-inset-bottom))' : 2,
                      borderTop: 1,
                      borderColor: 'divider',
                      display: 'flex',
                      justifyContent: 'center',
                      backgroundColor: theme.palette.grey[100],
                    }}
                  >
                    <Pagination
                      count={totalPages}
                      page={currentPage}
                      onChange={handlePageChange}
                      color='primary'
                      showFirstButton
                      showLastButton
                      size='small'
                    />
                  </Box>
                )}
              </>
            ) : (
              <Box
                sx={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  p: 4,
                  textAlign: 'center',
                }}
              >
                {isAiTyping || loading ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <Box sx={{
                      position: 'relative',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: 3
                    }}>
                      <CircularProgress
                        size={48}
                        thickness={3}
                        sx={{
                          color: theme.palette.ai.main,
                        }}
                      />
                    </Box>
                    <Typography
                      variant='h6'
                      sx={{
                        color: theme.palette.ai.dark,
                        fontWeight: 600,
                        mb: 1,
                      }}
                    >
                      Searching for jobs...
                    </Typography>
                    <Typography variant='body2' color='text.secondary'>
                      Stethi is analyzing opportunities for you...
                    </Typography>
                  </Box>
                ) : (
                  <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    maxWidth: 400,
                  }}>
                    <MyStethiAIIcon sx={{ fontSize: '2.5rem', color: theme.palette.ai.main, mb: 2, opacity: 0.5 }} />
                    <Typography variant='h6' sx={{ color: theme.palette.grey[800], fontWeight: 600, mb: 0.5 }}>
                      Ready to find your perfect job
                    </Typography>
                    <Typography variant='body2' sx={{ color: theme.palette.grey[600], mb: 3 }}>
                      Try one of these, or describe what you're looking for:
                    </Typography>
                    <Stack spacing={1} sx={{ width: '100%', mb: 3 }}>
                          {exampleQueries.map((query) => (
                            <Typography
                              key={query}
                              variant='body2'
                              onClick={() => handleExampleChipClick(query)}
                              sx={{
                                color: theme.palette.ai.dark,
                                cursor: 'pointer',
                                padding: '8px 12px',
                                borderRadius: 1,
                                border: `1px solid ${theme.palette.grey[200]}`,
                                transition: 'all 0.15s ease',
                                '&:hover': {
                                  borderColor: theme.palette.ai.main,
                                  color: theme.palette.ai.main,
                                  backgroundColor: `${theme.palette.ai.light}20`,
                                },
                              }}
                            >
                              "{query}"
                            </Typography>
                          ))}
                        </Stack>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Typography variant='caption' sx={{ color: theme.palette.grey[500], fontStyle: 'normal' }}>
                            Use the chat on the right to get started
                          </Typography>
                          <ArrowForwardIcon sx={{ fontSize: '0.875rem', color: theme.palette.grey[500] }} />
                    </Box>
                  </Box>
                )}
              </Box>
            )}
          </Box>
        </Grid>
      </Grid>

      {/* Job Modal */}
      <JobModal job={selectedJob?.job} rankedJob={selectedJob} onClose={handleCloseModal} conversationId={conversationId} />

      {/* Chat Bottom Bar (mobile only, when minimized) */}
      <ChatBottomBar />

      {/* AI Chat Drawer */}
      <AIChatBot />
    </>
  )
}

export default AIJobInterface
